import React, { useEffect, useState } from 'react';
import { Button, Table, Drawer, Form, Input, message, Divider, Select, Card, Tag, Space } from 'antd';
import { ticketAPI } from '../services/api';
import { PlusOutlined, MessageOutlined, CloseOutlined } from '@ant-design/icons';
import { TicketItem } from '../types/index';

import dayjs from 'dayjs';
import { showErrorToast, showToast } from '../utils/toast';

interface TicketDetail {
    id: number;
    user_id: number;
    subject: string;
    level: number;
    status: number;
    reply_status: number;
    created_at: number;
    updated_at: number;
    message: TicketItem[];
}

export const Ticket: React.FC = () => {
    const [tickets, setTickets] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [detailVisible, setDetailVisible] = useState(false);
    const [createVisible, setCreateVisible] = useState(false);
    const [currentTicket, setCurrentTicket] = useState<TicketDetail | null>(null);
    const [form] = Form.useForm();
    const [replyForm] = Form.useForm();

    // 获取工单列表
    const fetchTickets = async () => {
        setLoading(true);
        try {
            const res = await ticketAPI.getTickets();
            setTickets(res.data.data);
        } catch (error: any) {
            showErrorToast(error.response?.data?.message || '获取工单列表失败');
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchTickets();
    }, []);

    // 表格列配置
    const columns = [
        {
            title: '工单号',
            dataIndex: 'id',
            key: 'id',
        },
        {
            title: '主题',
            dataIndex: 'subject',
            key: 'subject',
        },
        // {
        //     title: '等级',
        //     dataIndex: 'level',
        //     key: 'level',
        //     render: (level: number) => {
        //         const levelMap = { 0: '低', 1: '中', 2: '高' };
        //         const colorMap = { 0: '#52c41a', 1: '#faad14', 2: '#ff4d4f' };
        //         return (
        //             <span style={{ color: colorMap[level as keyof typeof colorMap] }}>
        //                 {levelMap[level as keyof typeof levelMap]}
        //             </span>
        //         );
        //     },
        // },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            render: (status: number, record: any) => (
                <span>{status === 1 ? '已关闭' : record.reply_status === 0 ? '已回复' : '待处理'}</span>
            ),
        },
        {
            title: '创建时间',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (created_at: number) => dayjs(created_at * 1000).format('YYYY-MM-DD HH:mm:ss'),
        },
        {
            title: '操作',
            key: 'action',
            render: (_: any, record: any) => (
                <>
                    <Button type="link" onClick={() => showDetail(record)}>
                        查看
                    </Button>
                    {record.status !== 1 && (
                        <>
                            <Divider type="vertical" />
                            <Button type="link" onClick={() => handleClose(record)}>
                                关闭
                            </Button>
                        </>
                    )}
                </>
            ),
        },
    ];

    // 显示工单详情
    const showDetail = async (ticket: any) => {
        try {
            const res = await ticketAPI.getTicketDetail(ticket.id);
            setCurrentTicket(res.data.data);
            setDetailVisible(true);
        } catch (error: any) {
            showErrorToast(error.response?.data?.message || '获取工单详情失败');
        }
    };

    // 创建工单
    const handleCreate = async (values: any) => {
        try {
            await ticketAPI.createTicket(values);
            showToast('创建工单成功');
            setCreateVisible(false);
            form.resetFields();
            fetchTickets();
        } catch (error: any) {
            showErrorToast(error.response?.data?.message || '创建工单失败');
        }
    };

    // 回复工单
    const handleReply = async (values: any) => {
        try {
            await ticketAPI.replyTicket(currentTicket!.id, values.reply).then(() => {
                setDetailVisible(false);
                showToast('回复成功');
                replyForm.resetFields();
                fetchTickets();
            });
        } catch (error: any) {
            setDetailVisible(false);
            // replyForm.resetFields();
            console.log(error.response?.data?.message);
            showErrorToast(error.response?.data?.message || '回复失败');
        }
    };

    // 关闭工单
    const handleClose = async (ticket: any) => {
        try {
            await ticketAPI.closeTicket(ticket.id);
            showToast('关闭工单成功');
            setDetailVisible(false);
            fetchTickets();
        } catch (error) {
            showErrorToast('关闭工单失败');
        }
    };

    // 移动端卡片视图
    const renderMobileCard = (ticket: any) => {
        const getStatusColor = (status: number, reply_status: number) => {
            if (status === 1) return 'default';
            return reply_status === 0 ? 'success' : 'processing';
        };

        const getStatusText = (status: number, reply_status: number) => {
            if (status === 1) return '已关闭';
            return reply_status === 0 ? '已回复' : '待处理';
        };

        return (
            <Card
                key={ticket.id}
                className="mb-4 shadow-sm"
                size="small"
                title={
                    <div className="flex justify-between items-center">
                        <span className="text-sm font-medium truncate flex-1 mr-2">
                            #{ticket.id} {ticket.subject}
                        </span>
                        <Tag color={getStatusColor(ticket.status, ticket.reply_status)} className="text-xs">
                            {getStatusText(ticket.status, ticket.reply_status)}
                        </Tag>
                    </div>
                }
                extra={
                    <Space size="small">
                        <Button 
                            type="text" 
                            size="small" 
                            icon={<MessageOutlined />}
                            onClick={() => showDetail(ticket)}
                            className="text-blue-500"
                        />
                        {ticket.status !== 1 && (
                            <Button 
                                type="text" 
                                size="small" 
                                icon={<CloseOutlined />}
                                onClick={() => handleClose(ticket)}
                                className="text-red-500"
                            />
                        )}
                    </Space>
                }
            >
                <div className="text-xs text-gray-500">
                    创建时间: {dayjs(ticket.created_at * 1000).format('MM/DD HH:mm')}
                </div>
            </Card>
        );
    };

    return (
        <div className="px-4 sm:px-6 lg:px-8">
            <div className="mb-8">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white transition-colors">我的工单</h1>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 transition-colors">
                    提交问题并跟进客服回复进度。
                </p>
            </div>

            <div className="mb-4">
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setCreateVisible(true)}
                    block
                    size="large"
                    className="md:hidden"
                >
                    新建工单
                </Button>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setCreateVisible(true)}
                    className="hidden md:inline-flex"
                >
                    新建工单
                </Button>
            </div>

            {/* 桌面端表格视图 */}
            <div className="hidden md:block">
                <Table
                    columns={columns}
                    dataSource={tickets}
                    loading={loading}
                    rowKey="id"
                    scroll={{ x: 800 }}
                />
            </div>

            {/* 移动端卡片视图 */}
            <div className="md:hidden">
                {loading ? (
                    <div className="text-center py-8 text-gray-500">
                        加载中...
                    </div>
                ) : tickets.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                        暂无工单
                    </div>
                ) : (
                    tickets.map(renderMobileCard)
                )}
            </div>

            {/* 新建工单抽屉 */}
            <Drawer
                title="新建工单"
                width={window.innerWidth < 768 ? '100%' : 500}
                open={createVisible}
                onClose={() => setCreateVisible(false)}
                placement={window.innerWidth < 768 ? 'bottom' : 'right'}
                height={window.innerWidth < 768 ? '80%' : undefined}
            >
                <Form form={form} onFinish={handleCreate} layout="vertical">
                    <Form.Item
                        name="subject"
                        label="主题"
                        rules={[{ required: true, message: '请输入工单主题' }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="level"
                        label="工单等级"
                        rules={[{ required: true, message: '请选择工单等级' }]}
                        initialValue={0}
                    >
                        <Select placeholder="请选择工单等级">
                            <Select.Option value={0}>低</Select.Option>
                            <Select.Option value={1}>中</Select.Option>
                            <Select.Option value={2}>高</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="message"
                        label="内容"
                        rules={[{ required: true, message: '请输入工单内容' }]}
                    >
                        <Input.TextArea rows={4} />
                    </Form.Item>
                    <Form.Item>
                        <Button type="primary" htmlType="submit">
                            提交
                        </Button>
                    </Form.Item>
                </Form>
            </Drawer>

            {/* 工单详情抽屉 */}
            <Drawer
                title="工单详情"
                width={window.innerWidth < 768 ? '100%' : 500}
                open={detailVisible}
                onClose={() => setDetailVisible(false)}
                placement={window.innerWidth < 768 ? 'bottom' : 'right'}
                height={window.innerWidth < 768 ? '90%' : undefined}
            >
                {currentTicket && (
                    <>
                        <h3>{currentTicket.subject}</h3>
                        <p>状态: {currentTicket.status === 1 ? '已关闭' : currentTicket.reply_status === 0 ? '已回复' : '待处理'}</p>
                        <p>等级: <span style={{ color: currentTicket.level === 0 ? '#52c41a' : currentTicket.level === 1 ? '#faad14' : '#ff4d4f' }}>
                            {currentTicket.level === 0 ? '低' : currentTicket.level === 1 ? '中' : '高'}
                        </span></p>
                        <p>创建时间: {dayjs(currentTicket.created_at * 1000).format('YYYY-MM-DD HH:mm:ss')}</p>

                        <div style={{ 
                            marginTop: 16, 
                            marginBottom: 16, 
                            maxHeight: window.innerWidth < 768 ? '50vh' : '400px', 
                            overflowY: 'auto',
                            padding: '0 4px'
                        }}>
                            {currentTicket.message.map((item, index) => {
                                const isUser = item.user_id === currentTicket.user_id;
                                return (
                                    <div
                                        key={index}
                                        style={{
                                            marginBottom: 20,
                                            display: 'flex',
                                            flexDirection: !isUser ? 'row' : 'row-reverse',
                                            alignItems: 'flex-start',
                                            gap: '12px'
                                        }}
                                    >
                                        {/* 头像 */}
                                        <div style={{
                                            width: '36px',
                                            height: '36px',
                                            borderRadius: '50%',
                                            backgroundColor: isUser ? '#1890ff' : '#52c41a',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: 'white',
                                            fontSize: '14px',
                                            fontWeight: 'bold',
                                            flexShrink: 0
                                        }}>
                                            {isUser ? '我' : '客服'}
                                        </div>

                                        {/* 消息内容区域 */}
                                        <div style={{
                                            maxWidth: window.innerWidth < 768 ? '85%' : '70%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: !isUser ? 'flex-start' : 'flex-end'
                                        }}>
                                            {/* 时间戳 */}
                                            <div style={{
                                                fontSize: '12px',
                                                color: '#999',
                                                marginBottom: '4px',
                                                textAlign: !isUser ? 'left' : 'right'
                                            }}>
                                                {dayjs(item.created_at * 1000).format('MM/DD HH:mm')}
                                            </div>

                                            {/* 消息气泡 */}
                                            <div style={{
                                                backgroundColor: !isUser ? '#f0f0f0' : '#1890ff',
                                                color: !isUser ? '#333' : 'white',
                                                padding: window.innerWidth < 768 ? '10px 12px' : '12px 16px',
                                                borderRadius: !isUser ? '18px 18px 18px 4px' : '18px 18px 4px 18px',
                                                wordBreak: 'break-word',
                                                lineHeight: '1.4',
                                                position: 'relative',
                                                boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                                fontSize: window.innerWidth < 768 ? '14px' : '16px'
                                            }}>
                                                {item.message}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {currentTicket.status !== 1 && (
                            <div style={{ 
                                position: window.innerWidth < 768 ? 'sticky' : 'static',
                                bottom: window.innerWidth < 768 ? 0 : 'auto',
                                backgroundColor: 'white',
                                padding: window.innerWidth < 768 ? '16px 0' : '0',
                                borderTop: window.innerWidth < 768 ? '1px solid #f0f0f0' : 'none',
                                marginTop: 16
                            }}>
                                <Form form={replyForm} onFinish={handleReply} layout="vertical">
                                    <Form.Item
                                        name="reply"
                                        label="回复内容"
                                        rules={[{ required: true, message: '请输入回复内容' }]}
                                        style={{ marginBottom: 16 }}
                                    >
                                        <Input.TextArea 
                                            rows={window.innerWidth < 768 ? 3 : 4} 
                                            placeholder="请输入回复内容..."
                                        />
                                    </Form.Item>
                                    <Form.Item style={{ marginBottom: 0 }}>
                                        <Button 
                                            type="primary" 
                                            htmlType="submit" 
                                            block={window.innerWidth < 768}
                                            size={window.innerWidth < 768 ? 'large' : 'middle'}
                                        >
                                            发送回复
                                        </Button>
                                    </Form.Item>
                                </Form>
                            </div>
                        )}
                    </>
                )}
            </Drawer>
        </div>
    );
};

export default Ticket;
